import { lioboCustomer } from '@/features/customers';
import { ConfirmModal } from '@/features/subscriptions';
import { Loader } from '@/shared/components/common/Loader';
import { LogDetailsModal } from '@/shared/components/LogDetailsModal';
import { PageLayout, type BreadcrumbItem } from '@/shared/components/PageLayout';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import { cilArrowLeft, cilReload, cilTrash } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { brokenSubscriptionsApi } from '../api';
import { BROKEN_SUBSCRIPTION_EDIT_DENIED_QUERY } from '../constants';
import type { BrokenSubscriptionDetails } from '../types';
import type { DeleteTarget, PageMode } from './BrokenSubscriptionDetailPage.types';
import {
  formatDiscountAmount,
  formatNextOrderDate,
  formatPrice,
  isOrderNotDueYet,
  linesSubtotalBeforeDiscount,
  sortDiscountsForDisplay,
} from './BrokenSubscriptionDetailPage.utils';
import { PurrscribeProductRow } from './PurrscribeProductRow';
import { ShopifyLineRow } from './ShopifyLineRow';

type Props = {
  mode: PageMode;
};

export const BrokenSubscriptionDetailPage: React.FC<Props> = ({ mode }) => {
  const { shopifyCustomerId: paramId } = useParams<{ shopifyCustomerId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const shopifyCustomerId = paramId ? Number(paramId) : null;

  const [details, setDetails] = useState<BrokenSubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [reasonDetailsModalVisible, setReasonDetailsModalVisible] = useState(false);
  const [showEditRedirectNotice, setShowEditRedirectNotice] = useState(
    () => mode === 'view' && searchParams.get(BROKEN_SUBSCRIPTION_EDIT_DENIED_QUERY) === '1',
  );
  const { hasPermission } = usePermissions();
  const canLiobo = hasPermission(USER_PERMISSIONS.Liobo);
  const hasBrokenSubscriptionsEdit = hasPermission(USER_PERMISSIONS.BrokenSubscriptionsEdit);

  useEffect(() => {
    if (mode !== 'view') return;
    if (searchParams.get(BROKEN_SUBSCRIPTION_EDIT_DENIED_QUERY) !== '1') return;
    const next = new URLSearchParams(searchParams);
    next.delete(BROKEN_SUBSCRIPTION_EDIT_DENIED_QUERY);
    setSearchParams(next, { replace: true });
  }, [mode, searchParams, setSearchParams]);

  const fetchDetails = useCallback(async () => {
    if (shopifyCustomerId == null || Number.isNaN(shopifyCustomerId)) return;
    setLoading(true);
    setError(null);
    try {
      const data = await brokenSubscriptionsApi.getDetails(shopifyCustomerId);
      setDetails(data);
    } catch (e: unknown) {
      const message =
        e && typeof e === 'object' && 'message' in e ? String(e.message) : 'Failed to load';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [shopifyCustomerId]);

  useEffect(() => {
    void Promise.resolve().then(fetchDetails);
  }, [fetchDetails]);

  const handleRevalidate = async () => {
    if (shopifyCustomerId == null || mode !== 'edit' || !hasBrokenSubscriptionsEdit) return;
    setActionInProgress(true);
    setGlobalError(null);
    try {
      const data = await brokenSubscriptionsApi.revalidate(shopifyCustomerId);
      setDetails(data);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String(e.message) : 'Revalidation failed';
      setGlobalError(msg);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleCloseError = () => {
    setGlobalError(null);
    void fetchDetails();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || shopifyCustomerId == null) return;
    setDeleteInProgress(true);
    try {
      if (deleteTarget.type === 'shopify-line') {
        await brokenSubscriptionsApi.deleteShopifyLine(shopifyCustomerId, deleteTarget.lineId);
      } else if (deleteTarget.type === 'shopify-discount') {
        await brokenSubscriptionsApi.deleteShopifyDiscount(
          shopifyCustomerId,
          deleteTarget.discountId,
        );
      } else if (deleteTarget.type === 'product') {
        await brokenSubscriptionsApi.deleteProduct(shopifyCustomerId, deleteTarget.productId);
      } else {
        await brokenSubscriptionsApi.deleteDiscount(shopifyCustomerId, deleteTarget.discountId);
      }
      setDeleteTarget(null);
      await fetchDetails();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String(e.message) : 'Delete failed';
      setGlobalError(msg);
    } finally {
      setDeleteInProgress(false);
    }
  };

  const canEdit =
    mode === 'edit' && hasBrokenSubscriptionsEdit && !actionInProgress && !deleteInProgress;

  const detailBreadcrumbItems: BreadcrumbItem[] = [
    { label: 'Broken Subscriptions', path: '/broken-subscriptions' },
    { label: mode === 'edit' && hasBrokenSubscriptionsEdit ? 'Edit' : 'View' },
  ];

  if (shopifyCustomerId == null || Number.isNaN(shopifyCustomerId)) {
    return (
      <PageLayout>
        <CAlert color="danger">Invalid customer ID.</CAlert>
        <CButton color="secondary" onClick={() => void navigate('/broken-subscriptions')}>
          Back to Search
        </CButton>
      </PageLayout>
    );
  }

  if (loading && !details) {
    return (
      <PageLayout breadcrumbItems={detailBreadcrumbItems}>
        <div className="d-flex justify-content-center p-5">
          <Loader isLoading />
        </div>
      </PageLayout>
    );
  }

  if (error && !details) {
    return (
      <PageLayout>
        <CAlert color="danger">{error}</CAlert>
        <CButton color="secondary" onClick={() => void navigate('/broken-subscriptions')}>
          Back to Search
        </CButton>
      </PageLayout>
    );
  }

  if (!details) return null;

  const contractLines = [...(details.shopifyContract?.lines ?? [])].sort((a, b) =>
    (a.productName ?? '').localeCompare(b.productName ?? ''),
  );
  const contractDiscounts = sortDiscountsForDisplay(details.shopifyContract?.discounts ?? []);
  const validLineIds = new Set(contractLines.map((l) => String(l.lineId)));

  return (
    <PageLayout breadcrumbItems={detailBreadcrumbItems}>
      <div className="d-flex align-items-center gap-2 mb-3">
        <CButton
          color="secondary"
          variant="ghost"
          onClick={() => void navigate('/broken-subscriptions')}
        >
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Search
        </CButton>
      </div>

      {mode === 'edit' && !hasBrokenSubscriptionsEdit && (
        <CAlert color="danger" className="mb-3">
          You don&apos;t have permission to edit broken subscriptions.
        </CAlert>
      )}

      {mode === 'view' && showEditRedirectNotice && (
        <CAlert
          color="danger"
          className="mb-3"
          dismissible
          onClose={() => setShowEditRedirectNotice(false)}
        >
          You don&apos;t have permission to edit broken subscriptions.
        </CAlert>
      )}

      {actionInProgress && (
        <div className="position-fixed top-50 start-50 translate-middle z-3">
          <CSpinner />
        </div>
      )}

      <CRow>
        <CCol md={6}>
          <CCard className="mb-3">
            <CCardHeader>Customer details</CCardHeader>
            <CCardBody>
              <p className="mb-1">
                <strong>Shopify ID:</strong> {details.purrscribePlan.shopifyCustomerId}
              </p>
              <p className="mb-1">
                <strong>Name:</strong> {details.purrscribePlan.name ?? '-'}
              </p>
              <p className="mb-1">
                <strong>Email:</strong> {details.purrscribePlan.email ?? '-'}
              </p>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard className="mb-3">
            <CCardHeader>Subscription status</CCardHeader>
            <CCardBody>
              <p className="mb-1">
                <strong>Subscription Plan Status:</strong> {details.purrscribePlan.status ?? '-'}
              </p>
              <p className="mb-0">
                <strong>Next Order Date:</strong>{' '}
                {formatNextOrderDate(details.purrscribePlan.nextDispatchDate) ?? '-'}
                {isOrderNotDueYet(details.purrscribePlan.nextDispatchDate) &&
                  ' (Order is not due yet)'}
              </p>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard className="mb-3">
        <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <strong>Reason:</strong>{' '}
            {details.problem.reasonCode ?? (details.problem.isValid ? 'None (valid)' : 'Unknown')}
            {details.problem.extraData != null &&
              Object.keys(details.problem.extraData).length > 0 && (
                <>
                  {' '}
                  <CButton
                    color="link"
                    size="sm"
                    className="p-0 align-baseline"
                    onClick={() => setReasonDetailsModalVisible(true)}
                  >
                    Show more details
                  </CButton>
                </>
              )}
          </div>
        </CCardBody>
      </CCard>

      {reasonDetailsModalVisible && (
        <LogDetailsModal
          visible={reasonDetailsModalVisible}
          onClose={() => setReasonDetailsModalVisible(false)}
          title="Reason details"
          baseData={{ id: 0, createdAt: '-' }}
          additionalFields={[{ label: 'Reason', value: details.problem.reasonCode ?? '-' }]}
          jsonSections={[{ title: 'Extra Data', data: details.problem.extraData ?? {} }]}
        />
      )}

      {details.problem.isValid && (
        <CAlert color="success" className="mb-3">
          SUBSCRIPTION IS VALID
        </CAlert>
      )}

      {details.shopifyContract && (
        <CCard className="mb-3">
          <CCardHeader>Shopify subscription contract</CCardHeader>
          <CCardBody>
            <CTable responsive align="middle">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Product</CTableHeaderCell>
                  <CTableHeaderCell>Product ID</CTableHeaderCell>
                  <CTableHeaderCell>SKU</CTableHeaderCell>
                  <CTableHeaderCell>Shopify Line ID</CTableHeaderCell>
                  <CTableHeaderCell>Quantity</CTableHeaderCell>
                  <CTableHeaderCell>Price/Amount</CTableHeaderCell>
                  <CTableHeaderCell>Subtotal</CTableHeaderCell>
                  {canEdit && <CTableHeaderCell>Action</CTableHeaderCell>}
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {contractLines.map((line) => (
                  <ShopifyLineRow
                    key={String(line.lineId)}
                    line={line}
                    canEdit={canEdit}
                    shopifyCustomerId={shopifyCustomerId}
                    actionInProgress={actionInProgress}
                    onSave={(...args: Parameters<typeof fetchDetails>) =>
                      void fetchDetails(...args)
                    }
                    onError={setGlobalError}
                    onDelete={() =>
                      setDeleteTarget({ type: 'shopify-line', lineId: String(line.lineId) })
                    }
                  />
                ))}
                {contractDiscounts.map((discount) => (
                  <CTableRow key={String(discount.shopifyDiscountId)}>
                    <CTableDataCell colSpan={5} />
                    <CTableDataCell className="text-end">{discount.description}</CTableDataCell>
                    <CTableDataCell>
                      {formatDiscountAmount(discount.amount, discount.isPercentage)}
                    </CTableDataCell>
                    {canEdit && (
                      <CTableDataCell>
                        <CButton
                          color="danger"
                          size="sm"
                          variant="ghost"
                          aria-label="Delete Shopify discount"
                          title="Delete Shopify discount"
                          onClick={() =>
                            setDeleteTarget({
                              type: 'shopify-discount',
                              discountId: String(discount.shopifyDiscountId),
                            })
                          }
                        >
                          <CIcon icon={cilTrash} />
                        </CButton>
                      </CTableDataCell>
                    )}
                  </CTableRow>
                ))}
                {(() => {
                  const subtotalBeforeDiscount = linesSubtotalBeforeDiscount(contractLines);
                  return (
                    <>
                      <CTableRow>
                        <CTableDataCell colSpan={5} />
                        <CTableDataCell className="text-end">
                          <strong>Subtotal</strong>
                        </CTableDataCell>
                        <CTableDataCell>
                          <strong>{formatPrice(subtotalBeforeDiscount)}</strong>
                        </CTableDataCell>
                        {canEdit && <CTableDataCell />}
                      </CTableRow>
                      {details.shopifyContract.totalPrice != null && (
                        <CTableRow>
                          <CTableDataCell colSpan={5} />
                          <CTableDataCell className="text-end">
                            <strong>Total</strong>
                          </CTableDataCell>
                          <CTableDataCell>
                            <strong>{formatPrice(details.shopifyContract.totalPrice)}</strong>
                          </CTableDataCell>
                          {canEdit && <CTableDataCell />}
                        </CTableRow>
                      )}
                    </>
                  );
                })()}
              </CTableBody>
            </CTable>
          </CCardBody>
        </CCard>
      )}

      <CCard className="mb-3">
        <CCardHeader>Purrscribe subscription plan</CCardHeader>
        <CCardBody>
          {details.purrscribeSubscriptions.map((sub) => (
            <div key={sub.subscriptionId} className="mb-4">
              <h5>
                {sub.type === 'cat'
                  ? sub.catName
                    ? `Cat subscription for ${sub.catName} – ${sub.tinsCount} tin(s) per day`
                    : 'Cat subscription'
                  : sub.type === 'other'
                    ? 'Add-on subscription'
                    : 'Subscription'}
              </h5>
              <CTable responsive align="middle">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell className={canEdit ? 'col-3' : 'col-4'}>
                      Product
                    </CTableHeaderCell>
                    <CTableHeaderCell className="col-2">Product ID</CTableHeaderCell>
                    <CTableHeaderCell className="col-1">SKU</CTableHeaderCell>
                    <CTableHeaderCell className="col-3">Shopify Line ID</CTableHeaderCell>
                    <CTableHeaderCell className="col-1">Quantity</CTableHeaderCell>
                    <CTableHeaderCell className="col-1">Price</CTableHeaderCell>
                    {canEdit && <CTableHeaderCell className="col-1">Action</CTableHeaderCell>}
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {[...sub.products]
                    .sort((a, b) => (a.productName ?? '').localeCompare(b.productName ?? ''))
                    .map((product) => (
                      <PurrscribeProductRow
                        key={product.id}
                        product={product}
                        canEdit={canEdit}
                        validLineIds={validLineIds}
                        shopifyCustomerId={shopifyCustomerId}
                        actionInProgress={actionInProgress}
                        onSave={(...args: Parameters<typeof fetchDetails>) =>
                          void fetchDetails(...args)
                        }
                        onError={setGlobalError}
                        onDelete={() => setDeleteTarget({ type: 'product', productId: product.id })}
                      />
                    ))}
                </CTableBody>
              </CTable>
            </div>
          ))}
          {details.purrscribePlan.discounts.length > 0 && (
            <>
              <h6 className="mt-3">Discounts</h6>
              <CTable responsive align="middle">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Code</CTableHeaderCell>
                    <CTableHeaderCell>Shopify Discount ID</CTableHeaderCell>
                    <CTableHeaderCell>Type</CTableHeaderCell>
                    <CTableHeaderCell>Amount</CTableHeaderCell>
                    {canEdit && <CTableHeaderCell>Action</CTableHeaderCell>}
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {details.purrscribePlan.discounts.map((d) => (
                    <CTableRow key={d.id}>
                      <CTableDataCell>{d.discountCode}</CTableDataCell>
                      <CTableDataCell>{d.shopifyDiscountId ?? ''}</CTableDataCell>
                      <CTableDataCell>{d.type ?? ''}</CTableDataCell>
                      <CTableDataCell>
                        {formatDiscountAmount(d.amount, d.type === 'percent')}
                      </CTableDataCell>
                      {canEdit && (
                        <CTableDataCell>
                          <CButton
                            color="danger"
                            size="sm"
                            variant="ghost"
                            aria-label={`Delete discount ${d.discountCode}`}
                            title={`Delete discount ${d.discountCode}`}
                            onClick={() => setDeleteTarget({ type: 'discount', discountId: d.id })}
                          >
                            <CIcon icon={cilTrash} />
                          </CButton>
                        </CTableDataCell>
                      )}
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </>
          )}
        </CCardBody>
      </CCard>

      <CCard className="mb-3">
        <CCardBody className="d-flex flex-wrap gap-2 justify-content-end">
          {canLiobo && (
            <CButton
              type="button"
              color="primary"
              variant="outline"
              onClick={() => void lioboCustomer(shopifyCustomerId.toString())}
            >
              Log in as a Customer
            </CButton>
          )}
          {canEdit && (
            <CButton
              color="primary"
              onClick={(...args: Parameters<typeof handleRevalidate>) =>
                void handleRevalidate(...args)
              }
              disabled={actionInProgress}
            >
              <CIcon icon={cilReload} className="me-1" />
              Update/check subscription state
            </CButton>
          )}
        </CCardBody>
      </CCard>

      {globalError && (
        <CModal visible onClose={handleCloseError}>
          <CModalHeader>
            <CModalTitle>Update failed</CModalTitle>
          </CModalHeader>
          <CModalBody>The update failed. Reason: {globalError}</CModalBody>
          <CModalFooter>
            <CButton color="primary" onClick={handleCloseError}>
              OK
            </CButton>
          </CModalFooter>
        </CModal>
      )}

      {deleteTarget &&
        (() => {
          const isDiscount =
            deleteTarget.type === 'shopify-discount' || deleteTarget.type === 'discount';
          const label = isDiscount
            ? 'discount'
            : deleteTarget.type === 'product'
              ? 'product'
              : 'line';
          return (
            <ConfirmModal
              visible
              title={`Remove ${label}?`}
              message={`Are you sure you want to remove this ${label} (this is irreversible)?`}
              confirmText="Yes - remove"
              cancelText="No - cancel"
              confirmColor="danger"
              loading={deleteInProgress}
              onConfirm={(...args: Parameters<typeof handleDeleteConfirm>) =>
                void handleDeleteConfirm(...args)
              }
              onCancel={() => setDeleteTarget(null)}
            />
          );
        })()}
    </PageLayout>
  );
};
